import React, { useContext, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Popover,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useParams } from "react-router-dom";
import { PROJECT_STATUS, SET_PROJECT_STATUS } from "../../graphql/gqlProject";
import { SnackbarContext } from "../../contexts/snackbarContext";
import { T } from "../T";

type ProjectStatus = "PRIVATE" | "SHARED" | "PUBLIC";

const labelForStatus = (s: ProjectStatus) =>
  s === "PRIVATE" ? (
    <T str="Private" />
  ) : s === "SHARED" ? (
    <T str="Shared" />
  ) : (
    <T str="Public" />
  );

const warningFor = (s: ProjectStatus) => {
  switch (s) {
    case "SHARED":
      return (
        <T str="Anyone with the link can view this project. It will NOT appear in the public repository." />
      );
    case "PUBLIC":
      return (
        <T str="Anyone with the link can view this project. It WILL appear in the public repository and may be discovered by others." />
      );
    default:
      return (
        <T str="The project will become private. All previously shared access will be revoked." />
      );
  }
};

const buildProjectUrl = (projectId?: string) => {
  if (!projectId) return "";
  if (typeof window === "undefined") return `/projects/${projectId}`;
  return `${window.location.protocol}//${window.location.host}${location.pathname}`;
};

const ProjectStatusVisibility = () => {
  const { projectId } = useParams();
  const { openSuccessSnackbar, openErrorSnackbar } =
    useContext(SnackbarContext);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);
  const open = Boolean(anchorEl);

  const { data, loading, error } = useQuery(PROJECT_STATUS, {
    variables: { projectId },
    skip: !projectId,
  });

  const currentStatus = (
    data?.getProjectById?.status || "PRIVATE"
  ).toUpperCase() as ProjectStatus;
  const projectUrl = buildProjectUrl(projectId);

  const [selectedStatus, setSelectedStatus] =
    useState<ProjectStatus>(currentStatus);

  useEffect(() => {
    if (open) setSelectedStatus(currentStatus);
  }, [open, currentStatus]);

  const isSharedOrPublicSelected =
    selectedStatus === "SHARED" || selectedStatus === "PUBLIC";

  const [setProjectStatus, { loading: saving }] = useMutation(
    SET_PROJECT_STATUS,
    {
      onCompleted: (d) => {
        const s = d?.setProjectStatus?.status as ProjectStatus | undefined;
        if (!s) return;
        openSuccessSnackbar(
          s === "PUBLIC" ? (
            <T str="Project is now Public" />
          ) : s === "SHARED" ? (
            <T str="Project is now Shared" />
          ) : (
            <T str="Project is now Private" />
          )
        );
      },
      onError: (e) =>
        openErrorSnackbar(e.message || <T str="Failed to change status" />),
      refetchQueries: [{ query: PROJECT_STATUS, variables: { projectId } }],
      awaitRefetchQueries: true,
    }
  );

  if (!projectId || loading) return null;
  if (error) throw Error("Projects loading failed");

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApplyChange = async () => {
    if (selectedStatus === currentStatus) return;
    await setProjectStatus({
      variables: { projectId, status: selectedStatus },
      optimisticResponse: {
        setProjectStatus: {
          __typename: "Project",
          id: projectId,
          status: selectedStatus,
        },
      },
    });
    handleClose();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(projectUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      openErrorSnackbar(<T str="Could not copy link" />);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={handleOpen}
        disabled={saving}
        sx={{ textTransform: "none", overflow: "hidden" }}
        color="secondary"
      >
        <T str="Visibility" />
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, width: 360, maxWidth: "90vw" } }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle1">
            <T str="Visibility" />
          </Typography>

          <RadioGroup
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as ProjectStatus)}
          >
            {(["PRIVATE", "SHARED", "PUBLIC"] as ProjectStatus[]).map(
              (status) => (
                <FormControlLabel
                  key={status}
                  value={status}
                  control={
                    <Radio
                      color={
                        selectedStatus === status ? "secondary" : "default"
                      }
                      sx={{
                        color: (theme) =>
                          selectedStatus === status
                            ? theme.palette.secondary.main
                            : theme.palette.text.primary,
                        "&.Mui-checked": {
                          color: (theme) => theme.palette.secondary.main,
                        },
                      }}
                    />
                  }
                  label={labelForStatus(status)}
                  disabled={saving}
                />
              )
            )}
          </RadioGroup>

          <Alert
            severity={selectedStatus === "PRIVATE" ? "info" : "warning"}
            variant="outlined"
          >
            {warningFor(selectedStatus)}
          </Alert>

          {isSharedOrPublicSelected && (
            <>
              <Divider />
              <Typography variant="subtitle2">
                {selectedStatus === "PUBLIC" ? (
                  <T str="Public URL" />
                ) : (
                  <T str="Shared URL" />
                )}
              </Typography>
              <TextField
                size="small"
                value={projectUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={copied ? <T str="Copied!" /> : <T str="Copy" />}
                      >
                        <span>
                          <IconButton
                            aria-label="copy url"
                            onClick={handleCopy}
                            edge="end"
                            disabled={!projectUrl || saving}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={1} pt={1}>
            <Button onClick={handleClose} sx={{ textTransform: "none" }}>
              <T str="Close" />
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyChange}
              disabled={saving || selectedStatus === currentStatus}
              sx={{ textTransform: "none" }}
              color="secondary"
            >
              <T str="Update" />
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default ProjectStatusVisibility;
